import React, { useState, useMemo } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Add, Edit, Delete, Home, Visibility } from '@mui/icons-material';
import { Classe, Matiere } from '../../types';
import MatiereForm from '../matieres/MatiereForm';
import ClasseForm from './ClasseForm';
import ClasseDetail from './ClasseDetail';
import MatiereDetail from '../matieres/MatiereDetail';
import { useClasse, useMatieres, useUpdateClasse, useDeleteMatiere, useCreateMatiere, useUpdateMatiere } from '../../hooks/useData';
import { useNotify } from '../../contexts/NotificationContext';

const ClasseView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { environment: env } = useEnvironment();

  const [selectedMatiere, setSelectedMatiere] = useState<Matiere | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [matiereToDelete, setMatiereToDelete] = useState<string | null>(null);
  const [openClasseForm, setOpenClasseForm] = useState(false);
  const { notify } = useNotify('content');

  // React Query hooks
  const { data: classe, isLoading: loadingClasse } = useClasse(env, id || '');
  const { data: allMatieres = [], isLoading: loadingMatieres } = useMatieres(env);
  const updateClasseMutation = useUpdateClasse();
  const deleteMatiereMutation = useDeleteMatiere();
  const createMatiereMutation = useCreateMatiere();
  const updateMatiereMutation = useUpdateMatiere();

  const loading = loadingClasse || loadingMatieres;

  // Filtrer les matières qui appartiennent à cette classe
  const matieres = useMemo(() => {
    return allMatieres.filter(m => m.classeId === id);
  }, [allMatieres, id]);

  const handleCreate = () => {
    setSelectedMatiere(null);
    setOpenForm(true);
  };

  const handleEdit = (matiere: Matiere) => {
    setSelectedMatiere(matiere);
    setOpenForm(true);
  };

  const handleViewDetail = (matiere: Matiere) => {
    setSelectedMatiere(matiere);
    setOpenDetail(true);
  };

  const handleDelete = async (matiereId: string) => {
    try {
      await deleteMatiereMutation.mutateAsync({ id: matiereId, env });
      notify('Matière supprimée avec succès', 'success');
    } catch (error) {
      notify('Erreur lors de la suppression', 'error');
    }
    setOpenDelete(false);
    setMatiereToDelete(null);
  };

  const handleFormSubmit = async (data: Omit<Matiere, 'id'>) => {
    try {
      // S'assurer que classeId est défini
      const matiereData = { ...data, classeId: id };

      if (selectedMatiere) {
        await updateMatiereMutation.mutateAsync({ id: selectedMatiere.id, data: matiereData, env });
        notify('Matière mise à jour avec succès', 'success');
      } else {
        await createMatiereMutation.mutateAsync({ data: matiereData as Omit<Matiere, 'id'>, env });
        notify('Matière créée avec succès', 'success');
      }
      setOpenForm(false);
    } catch (error) {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleMatiereClick = (matiereId: string) => {
    navigate(`/matieres/${matiereId}`);
  };

  const handleClasseFormSubmit = async (data: Omit<Classe, 'id'>) => {
    if (!id) return;

    try {
      await updateClasseMutation.mutateAsync({ id, data, env });
      notify('Classe mise à jour avec succès', 'success');
      setOpenClasseForm(false);
    } catch (error) {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'nom',
      headerName: 'Nom',
      width: 250,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleMatiereClick(params.row.id)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'nomCourt',
      headerName: 'Nom court',
      width: 120
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 100
    },
    {
      field: 'ordre',
      headerName: 'Ordre',
      width: 80
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => {
        const statut = params.value || 'published';
        return (
          <Chip
            label={statut === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
            color={statut === 'draft' ? 'warning' : 'success'}
            size="small"
          />
        );
      }
    },
    {
      field: 'estActif',
      headerName: 'Actif',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Oui' : 'Non'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="Voir"
          onClick={() => handleViewDetail(params.row)}
        />,
        <GridActionsCellItem
          icon={<Edit />}
          label="Modifier"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Supprimer"
          onClick={() => {
            setMatiereToDelete(params.row.id);
            setOpenDelete(true);
          }}
          showInMenu
        />,
      ],
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!classe) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5">Classe non trouvée</Typography>
        <Button onClick={() => navigate('/classes')} sx={{ mt: 2 }}>
          Retour aux classes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Accueil
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/classes')}
          sx={{ cursor: 'pointer' }}
        >
          Classes
        </Link>
        <Typography color="text.primary">{classe.nom}</Typography>
      </Breadcrumbs>

      {/* Informations de la classe */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h5" component="h1">
            {classe.nom}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => setOpenClasseForm(true)}
          >
            Modifier
          </Button>
        </Box>
        <ClasseDetail classe={classe} />
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Liste des matières */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Matières de cette classe ({matieres.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nouvelle Matière
        </Button>
      </Box>

      <Paper sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={matieres}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Formulaire de création/édition */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMatiere ? 'Modifier la matière' : 'Nouvelle matière'}
        </DialogTitle>
        <DialogContent>
          <MatiereForm
            matiere={selectedMatiere}
            defaultClasseId={id}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de vue détaillée */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Détails de la matière
        </DialogTitle>
        <DialogContent>
          {selectedMatiere && <MatiereDetail matiere={selectedMatiere} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)}>
            Fermer
          </Button>
          <Button
            onClick={() => {
              setOpenDetail(false);
              if (selectedMatiere) {
                handleEdit(selectedMatiere);
              }
            }}
            variant="contained"
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette matière ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Annuler</Button>
          <Button
            onClick={() => matiereToDelete && handleDelete(matiereToDelete)}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formulaire de modification de la classe */}
      <Dialog open={openClasseForm} onClose={() => setOpenClasseForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier la classe</DialogTitle>
        <DialogContent>
          <ClasseForm
            classe={classe}
            onSubmit={handleClasseFormSubmit}
            onCancel={() => setOpenClasseForm(false)}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default ClasseView;
